import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiDelete, apiGet, apiPost } from '../lib/api';
import { formatDate } from '../lib/format';
import { SkeletonList } from '../components/Skeleton';
import { toast } from '../lib/toast';

interface Rule {
  id: number;
  day_of_week: number;
  start_time: string;
  end_time: string;
}

interface Blackout {
  id: number;
  start_at: string;
  end_at: string;
  reason: string | null;
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function SettingsHours() {
  const [rules, setRules] = useState<Rule[] | null>(null);
  const [blackouts, setBlackouts] = useState<Blackout[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Add-rule form
  const [addDow, setAddDow] = useState(1);
  const [addStart, setAddStart] = useState('09:00');
  const [addEnd, setAddEnd] = useState('17:00');

  // Add-blackout form
  const [bStart, setBStart] = useState('');
  const [bEnd, setBEnd] = useState('');
  const [bReason, setBReason] = useState('');

  // Auto-fill end date when start changes if end is blank or before new start
  function onStartChange(v: string) {
    setBStart(v);
    if (!bEnd || bEnd < v) setBEnd(v);
  }

  async function load() {
    try {
      const [r, b] = await Promise.all([
        apiGet<{ rules: Rule[] }>('/api/availability/rules'),
        apiGet<{ blackouts: Blackout[] }>('/api/availability/blackouts'),
      ]);
      setRules(r.rules);
      setBlackouts(b.blackouts);
    } catch (e) {
      setErr((e as Error).message);
    }
  }
  useEffect(() => { load(); }, []);

  async function addRule() {
    try {
      await apiPost('/api/availability/rules', {
        day_of_week: addDow, start_time: addStart, end_time: addEnd,
      });
      toast.success('Hours added.');
      load();
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  async function removeRule(id: number) {
    try {
      await apiDelete(`/api/availability/rules/${id}`);
      toast.success('Removed.');
      load();
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  async function addBlackout() {
    if (!bStart) return;
    const endDate = bEnd || bStart;
    if (endDate < bStart) {
      toast.error('End date must be on or after start date.');
      return;
    }
    try {
      await apiPost('/api/availability/blackouts', {
        start_at: `${bStart} 00:00:00`,
        end_at: `${endDate} 23:59:59`,
        reason: bReason.trim() || undefined,
      });
      setBStart(''); setBEnd(''); setBReason('');
      toast.success('Blackout added.');
      load();
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  async function removeBlackout(id: number) {
    try {
      await apiDelete(`/api/availability/blackouts/${id}`);
      toast.success('Blackout removed.');
      load();
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  // Group rules by day
  const rulesByDow: Record<number, Rule[]> = {};
  for (const r of rules || []) (rulesByDow[r.day_of_week] ||= []).push(r);

  if (err) return <div className="text-rose-400">{err}</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">Hours</h1>
        <Link to="/settings" className="text-sm text-muted">← Settings</Link>
      </div>

      <h2 className="font-semibold text-text/90">Weekly schedule</h2>
      {!rules ? (
        <SkeletonList count={7} variant="row" />
      ) : (
        <div className="space-y-2">
          {DAY_NAMES.map((label, dow) => {
            const items = rulesByDow[dow] || [];
            return (
              <div key={dow} className="card">
                <div className="flex items-baseline justify-between mb-1">
                  <span className="font-semibold">{label}</span>
                  {items.length === 0 && <span className="text-muted text-sm">closed</span>}
                </div>
                {items.map((r) => (
                  <div key={r.id} className="flex items-center justify-between text-sm py-1">
                    <span className="font-mono">{r.start_time} – {r.end_time}</span>
                    <button onClick={() => removeRule(r.id)} className="text-rose-400 text-xs">remove</button>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      <div className="card space-y-3">
        <h3 className="font-semibold">Add hours</h3>
        <div>
          <label className="label">Day</label>
          <select className="input" value={addDow} onChange={(e) => setAddDow(parseInt(e.target.value, 10))}>
            {DAY_NAMES.map((label, dow) => <option key={dow} value={dow}>{label}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div><label className="label">Start</label><input className="input" type="time" value={addStart} onChange={(e) => setAddStart(e.target.value)} /></div>
          <div><label className="label">End</label><input className="input" type="time" value={addEnd} onChange={(e) => setAddEnd(e.target.value)} /></div>
        </div>
        <button onClick={addRule} className="btn-primary w-full">Add</button>
      </div>

      <h2 className="font-semibold text-text/90 mt-6">Blackouts (days off)</h2>
      {!blackouts ? (
        <SkeletonList count={2} variant="row" />
      ) : blackouts.length === 0 ? (
        <p className="text-muted text-sm">No blackouts scheduled.</p>
      ) : (
        <div className="space-y-2">
          {blackouts.map((b) => {
            const startDay = b.start_at.split(' ')[0];
            const endDay = b.end_at.split(' ')[0];
            const sameDay = startDay === endDay;
            const days = Math.round(
              (new Date(endDay).getTime() - new Date(startDay).getTime()) / 86400000
            ) + 1;
            return (
              <div key={b.id} className="card flex items-center justify-between">
                <div>
                  <div className="font-semibold">
                    {formatDate(b.start_at)}
                    {!sameDay && <> <span className="text-muted">→</span> {formatDate(b.end_at)}</>}
                  </div>
                  {!sameDay && <div className="text-xs text-muted"><span className="font-mono">{days}</span> days</div>}
                  {b.reason && <div className="text-sm text-muted">{b.reason}</div>}
                </div>
                <button onClick={() => removeBlackout(b.id)} className="text-rose-400 text-sm">remove</button>
              </div>
            );
          })}
        </div>
      )}

      <div className="card space-y-3">
        <h3 className="font-semibold">Add blackout</h3>
        <div className="grid grid-cols-2 gap-2">
          <div><label className="label">From</label><input className="input" type="date" value={bStart} onChange={(e) => onStartChange(e.target.value)} /></div>
          <div><label className="label">To</label><input className="input" type="date" value={bEnd} min={bStart} onChange={(e) => setBEnd(e.target.value)} /></div>
        </div>
        <p className="text-xs text-muted">Leave "To" same as "From" for a single day off.</p>
        <div><label className="label">Reason (optional)</label><input className="input" placeholder="e.g. Vacation, vet appointment" value={bReason} onChange={(e) => setBReason(e.target.value)} /></div>
        <button onClick={addBlackout} disabled={!bStart} className="btn-primary w-full disabled:opacity-40">Add</button>
      </div>
    </div>
  );
}
